import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchVendorDetailsRequest,
  resetVendorState,
  updateVendorApprovalStatusRequest,
} from "../../redux/Vendors/actions/vendorActions";
import {
  convertVendorToCustomerRequest,
  clearConvertVendorToCustomerStatus,
  convertRoleRequest,
  clearConvertRoleStatus,
} from "../../redux/Customers/actions/customerActions";
import { DialogActions, Button as MuiButton } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import "../../styles/vendorStyle.css";
import TextInputField from "../../component/ui/FormFields/TextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import { VENDORS } from "../../component/common/Routes/AppRoutes";
import { useForm } from "react-hook-form";
import Loading from "../../component/ui/Loading";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import DocumentViewer from "./DocumentViewer";

const ViewVendor = () => {
  const { vendorId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.state.tab ?? 0;
  const page = location.state.page ?? 1;
  const searchQuery = location?.state?.searchQuery ?? "";

  const vendorDetailsState = useSelector(
    (state) => state.vendors.vendorDetails,
  );
  const [rejectionReason, setRejectionReason] = useState("");

  const user = useSelector((state) => state.auth.user);
  const [openConvertModal, setOpenConvertModal] = useState(false);

  const convertRoleState = useSelector(
    (state) =>
      state.customers.convertRole || {
        loading: false,
        success: false,
        error: null,
      },
  );

  const convertState = useSelector(
    (state) =>
      state.customers.convertVendorToCustomer || {
        loading: false,
        success: false,
        error: null,
      },
  );
  console.log("convertState", convertState);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === "string"
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const [approvalStatus, setApprovalStatus] = useState("");
  const { control } = useForm();
  const vendorDetails = vendorDetailsState?.data || {};
  const viewLoading = vendorDetailsState?.loading || false;
  const approvalSuccess = vendorDetailsState?.approval_message;
  const [initialStatus, setInitialStatus] = useState("");

  useEffect(() => {
    if (vendorId) {
      dispatch(fetchVendorDetailsRequest(vendorId));
    }
  }, [dispatch, vendorId]);
  useEffect(() => {
    if (convertState.success) {
      toast.success(
        convertState.message || "Converted to Customer successfully!",
        {
          position: "top-center",
          autoClose: 3000,
          onClose: () => {
            setOpenConvertModal(false);
            dispatch(clearConvertVendorToCustomerStatus());
            navigate("/vendors");
          },
        },
      );
    }

    if (convertState.error) {
      toast.error(`Error: ${convertState.error}`, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(clearConvertVendorToCustomerStatus());
        },
      });
    }
  }, [convertState, dispatch, navigate]);

  useEffect(() => {
    if (convertRoleState.success) {
      toast.success(
        convertRoleState.message || "Role converted successfully!",
        {
          position: "top-center",
          autoClose: 3000,
          onClose: () => {
            dispatch(clearConvertRoleStatus());
            navigate("/vendors");
          },
        },
      );
    }

    if (convertRoleState.error) {
      toast.error(`Error: ${convertRoleState.error}`, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(clearConvertRoleStatus());
        },
      });
    }
  }, [convertRoleState, dispatch, navigate]);

  useEffect(() => {
    if (approvalSuccess) {
      toast.success("Approval Status Changed", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          resetVendorState();
          navigate(VENDORS);
        },
      });
    }
  }, [approvalSuccess]);
  useEffect(() => {
    if (vendorDetails?.approove_status) {
      setApprovalStatus(vendorDetails?.approove_status);
      setInitialStatus(vendorDetails?.approove_status);
    }
  }, [vendorDetails]);

  const {
    agency_name,
    first_name,
    last_name,
    email,
    phone,
    address1,
    address2,
    city_name,
    country_code,
    state_name,
    country_name,
    pincode,
    vendor_serial_no,
    pan_number,
    gstin_number,
    commission_rate,
    iata_reg_no,
    company_reg_number,
    proof_image,
    rejection_reason,
    discount,
  } = vendorDetails || {};

  const baseVendorFields = [
    {
      label: "Vendor Serial Number",
      value: vendor_serial_no || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Agency / Company Name",
      value: agency_name || "-",
      icon: <BusinessIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "First Name",
      value: first_name || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Last Name",
      value: last_name || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Email",
      value: email || "-",
      icon: <EmailIcon />,
      xs: 12,
      sm: 6,
    },
    {
      name: "country_code",
      value: country_code || "-",
      label: "code",
      sm: 2,
      xs: 4,
    },
    {
      label: "Phone",
      value: phone || "-",
      icon: <PhoneIcon />,
      xs: 8,
      sm: 4,
    },
    {
      label: "Address1",
      value: address1 || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Address2",
      value: address2 || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Country",
      value: country_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },

    {
      label: "State",
      value: state_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },

    {
      label: "City",
      value: city_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Pincode",
      value: pincode || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "Vendor Commission",
      value: discount || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6,
    },
    {
      label: "IATA Reg Number",
      value: iata_reg_no || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6,
    },
  ];

  const isIndiaVendor = country_name?.toLowerCase() === "india";
  const vendorFields = [
    ...baseVendorFields,
    ...(isIndiaVendor
      ? [
          {
            label: "PAN Number",
            value: pan_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6,
          },
          {
            label: "GSTIN Number",
            value: gstin_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6,
          },
        ]
      : [
          {
            label: "Company Registration Number",
            value: company_reg_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6,
          },
        ]),
  ];

  const vendorStatusMap = {
    1: "Active",
    0: "Inactive",
  };

  const vendorStatus = vendorStatusMap[vendorDetails.status] || "Unknown";

  const handleApprovalStatusChange = (e) => {
    setApprovalStatus(e.target.value);
    if (e.target.value !== "3") {
      setRejectionReason("");
    }
  };

  const handleApprovalStatusSubmit = async () => {
    if (approvalStatus === "3" && !rejectionReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }

    const payload = {
      vendor_id: vendorDetails.id,
      status: approvalStatus,
      rejection_reason: approvalStatus === "3" ? rejectionReason : "",
    };

    try {
      await dispatch(updateVendorApprovalStatusRequest(payload));
      await dispatch(fetchVendorDetailsRequest(vendorId));
    } catch (error) {
      console.error("Error updating vendor approval status:", error);
    }
  };
  const showPendingOption = initialStatus === "2" || initialStatus === 2;
  if (viewLoading) {
    return <Loading text="Loading Vendor details..." />;
  }
  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="view-vendor-card">
          <CardContent>
            <Typography variant="h5" className="form-heading">
              View Vendor
            </Typography>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 3 }}
              className="vendor-form-margin"
            >
              {vendorFields.map((field, index) => (
                <Grid item xs={field.xs} sm={field.sm} key={index}>
                  <TextInputField
                    label={field.label}
                    value={field.value || "N/A"}
                    disabled={true}
                    startAdornment={
                      <InputAdornment position="start">
                        {field.icon}
                      </InputAdornment>
                    }
                  />
                </Grid>
              ))}

              {vendorDetails?.proof_image && (
                <DocumentViewer fileUrl={vendorDetails?.proof_image} />
              )}

              <Grid item xs={12} sm={6}>
                <StatusRadioGroup
                  name={"status"}
                  control={control}
                  defaultValue={vendorStatus}
                  disabled={true}
                />
              </Grid>

              {(user?.role === "Admin" || user?.role_name === "Manager") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Approval Status
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    alignItems="center"
                  >
                    <Grid item>
                      <RadioGroup
                        value={approvalStatus}
                        onChange={handleApprovalStatusChange}
                        row
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio color="success" />}
                          label="Approved"
                        />
                        {showPendingOption && (
                          <FormControlLabel
                            value={2}
                            control={<Radio color="warning" />}
                            label="Pending"
                          />
                        )}
                        <FormControlLabel
                          value={3}
                          control={<Radio color="error" />}
                          label="Rejected"
                        />
                      </RadioGroup>
                    </Grid>
                    <Grid item>
                      <CustomButton
                        variant="contained"
                        className="success-button"
                        text="Submit Approval Status"
                        onClick={handleApprovalStatusSubmit}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            {approvalStatus == "3" && (
              <Grid item xs={12}>
                <Box className="rejection-reason-box">
                  <Typography variant="subtitle1" color="error" gutterBottom>
                    Rejection Reason
                  </Typography>
                  <TextInputField
                    value={vendorDetails?.rejection_reason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    required
                    multiline
                    rows={3}
                    placeholder="Enter reason for rejection..."
                  />
                </Box>
              </Grid>
            )}

            {/* {vendorDetails?.approove_status == '3' &&
              vendorDetails?.rejection_reason && (
                <Grid item xs={12}>
                  <Box className='rejection-reason-box'>
                    <Typography variant='subtitle1' color='error' gutterBottom>
                      Rejection Note
                    </Typography>
                    <Typography variant='body1'>
                      {vendorDetails?.rejection_reason}
                    </Typography>
                  </Box>
                </Grid>
              )} */}

            <Box
              className="button-container"
              sx={{ justifyContent: "space-between" }}
            >
              <Box>
                {vendorDetails.current_role === 2 &&
                !vendorDetails.is_converted ? (
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    text="Convert to Customer"
                    onClick={() => setOpenConvertModal(true)}
                  />
                ) : (
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    text="Convert to Customer"
                    loading={convertRoleState.loading}
                    onClick={() => {
                      dispatch(
                        convertRoleRequest({
                          vendor_id: vendorId,
                        }),
                      );
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: "16px" }}>
                <CustomButton
                  variant="contained"
                  className="cancel-button"
                  text="Back"
                  onClick={() =>
                    navigate(`/vendors`, {
                      state: { tab: tab, page: page, searchQuery },
                    })
                  }
                />

                {(permissions?.vendors?.edit || user?.user_type === 1) && (
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    text="Edit"
                    onClick={() =>
                      navigate(`/vendors/edit/${vendorDetails.id}`)
                    }
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Convert to Customer Confirmation Dialog */}
        <Dialog
          open={openConvertModal}
          onClose={() => setOpenConvertModal(false)}
        >
          <DialogTitle>Convert Vendor to Customer</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to convert this vendor to a customer?
            </Typography>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setOpenConvertModal(false)}>
              Cancel
            </MuiButton>
            <MuiButton
              onClick={() => dispatch(convertVendorToCustomerRequest(vendorId))}
              variant="contained"
              className="success-button"
              disabled={convertState.loading}
            >
              {convertState.loading ? "Converting..." : "Convert"}
            </MuiButton>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ViewVendor;
